import { type DataFlowStream, type Transformation } from 'app/model/dataflow/transformation';
import { type DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { type SapOdpSettings } from 'app/model/dataflow/dataset-settings/sap-odp-settings';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { Metamodel, ModelConstants, type Pipeline, ReadBehavior, stringIsNullOrEmpty } from 'shell-models-lib';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { DataflowFormatRegistryService } from 'app/registries/dataflow/dataflow-format-registry.service';
import { DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { type IDataFlowArguments } from 'app/model/dataflow/dataflow-expression-utilities';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { GlobalVariables } from 'app/services/globals';
import { asyncUpdateIdentifiers, updateIdentifiers } from 'app/model/dataflow/dataflow-expression-update-identifiers';
import { type ColumnReference } from 'app/model/dataset/columnReference';
import { DataFlowTypesUtils } from 'app/model/dataflow/dataflow-type-utilities';
import {
    AnyDataFlowType,
    IDataFlowDataType,
    StringDataFlowType
} from 'worker/app-workers/shared/models/dataflow-datatype';
import { CosmosDBStoreType, CosmosSqlSettings } from 'app/model/dataflow/dataset-settings/cosmos-sql-settings';
import { DataFlowUtils } from 'app/authoring/component/transformations/dataFlow-utils';
import { type DataFlow } from 'app/model/dataflow/dataflow';
import { type ExecuteDataFlowActivity } from 'app/model/pipeline/execute-dataflow.activity';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { type ModelService } from 'app/model/services/model.service';
import { MessagingService, MessagingServiceChannel } from 'app/services/messaging/messaging-service';
import { Message, MessageKind } from 'app/services/messaging/message';
import { noop } from 'lodash';
import { type SapODataSettings } from 'app/model/dataflow/dataset-settings/sap-odata-settings';

export const findInputTransformation = (transformation: Transformation): Transformation | null => {
    return (transformation.inputStreams.values().next().value as DataFlowStream)?.producer ?? null;
};

export const findOutputTransformation = (transformation: Transformation): Transformation | null => {
    return (transformation.outputStreams.values().next().value as DataFlowStream)?.producer ?? null;
};

export const findDataFlowSource = (transformation: Transformation): DataFlowSource | null => {
    while (transformation && !(Metamodel.modelOf(transformation).name === ModelConstants.dataFlowSource)) {
        transformation = findInputTransformation(transformation);
    }
    return transformation as DataFlowSource | null;
};

export const findDataFlowSink = (transformation: Transformation): DataFlowSink | null => {
    while (transformation && !(Metamodel.modelOf(transformation).name === ModelConstants.dataFlowSink)) {
        transformation = findOutputTransformation(transformation);
    }
    return transformation as DataFlowSink | null;
};

export const isSourceTypeEq =
    (linkedService: string, dataset: string, settings?: string) => (source: DataFlowSource) => {
        if (!source) {
            return false;
        }
        return source.isInlineDataset
            ? source.sourceLinkedService
                ? Metamodel.modelOf(source.sourceLinkedService).name === linkedService
                : !stringIsNullOrEmpty(settings) && Metamodel.modelOf(source.datasetSettings)?.name === settings
            : source.sourceDataset
            ? Metamodel.modelOf(source.sourceDataset).name === dataset
            : false;
    };

export const isSinkTypeEq = (linkedService: string, dataset: string, settings?: string) => (sink: DataFlowSink) => {
    if (!sink) {
        return false;
    }
    return sink.isInlineDataset
        ? sink.sinkLinkedService
            ? Metamodel.modelOf(sink.sinkLinkedService).name === linkedService
            : !stringIsNullOrEmpty(settings) && Metamodel.modelOf(sink.datasetSettings)?.name === settings
        : sink.sinkDataset
        ? Metamodel.modelOf(sink.sinkDataset).name === dataset
        : false;
};

export const isSapCDCEnabled = (source: DataFlowSource): boolean =>
    source.isInlineDataset
        ? (source.datasetSettings as SapOdpSettings | SapODataSettings)?.runMode !== ReadBehavior.FULL_ON_EVERY_RUN
        : source.sapRunMode !== ReadBehavior.FULL_ON_EVERY_RUN;

export const isSapOdpSource: (source: DataFlowSource) => boolean = isSourceTypeEq(
    adfModelTypeMap.sapOdp.linkedServiceType,
    adfModelTypeMap.sapOdp.datasetType,
    adfModelTypeMap.sapOdp.dataflowSettings
);

export const isSapODataSource: (source: DataFlowSource) => boolean = isSourceTypeEq(
    adfModelTypeMap.sapOData.linkedServiceType,
    adfModelTypeMap.sapOData.datasetType,
    adfModelTypeMap.sapOData.dataflowSettings
);

const isSqlServerSource: (source: DataFlowSource) => boolean = isSourceTypeEq(
    adfModelTypeMap.sqlServer.linkedServiceType,
    adfModelTypeMap.sqlServer.datasetType
);

const isAzureSqlSource: (source: DataFlowSource) => boolean = isSourceTypeEq(
    adfModelTypeMap.azureSql.linkedServiceType,
    adfModelTypeMap.azureSql.datasetType
);

const isSqlMISource: (source: DataFlowSource) => boolean = isSourceTypeEq(
    adfModelTypeMap.azureSqlMI.linkedServiceType,
    adfModelTypeMap.azureSqlMI.datasetType
);

const isSnowflakeTypeSource: (source: DataFlowSource) => boolean = isSourceTypeEq(
    adfModelTypeMap.snowflake.linkedServiceType,
    adfModelTypeMap.snowflake.datasetType
);

export const isEventHubsSource: (source: DataFlowSource) => boolean = isSourceTypeEq(
    adfModelTypeMap.eventHubs.linkedServiceType,
    adfModelTypeMap.eventHubs.datasetType,
    adfModelTypeMap.eventHubs.dataflowSettings
);

export const isSqlNativeCDCEnabled = (source: DataFlowSource) =>
    (isSqlServerSource(source) || isAzureSqlSource(source) || isSqlMISource(source)) && source.enableNativeCdc;

export const isSnowflakeNativeCDCEnabled = (source: DataFlowSource) =>
    isSnowflakeTypeSource(source) && source.enableNativeCdc;

export const isDataflowSourceEnableCdc = (source: DataFlowSource): boolean => {
    if (!source) {
        return false;
    }
    const isSapCDC = isSapOdpSource(source) || isSapODataSource(source) ? isSapCDCEnabled(source) : false;
    const isSqlCdc = isSqlNativeCDCEnabled(source);
    const isSnowflakeNativeCdc = isSnowflakeNativeCDCEnabled(source);
    return Boolean(isSapCDC) || Boolean(isSqlCdc?.expression === 'true') || Boolean(isSnowflakeNativeCdc?.expression === 'true');
};

export const isCosmosDBAnalyticalType = (source: DataFlowSource) => {
    if (isSourceTypeEq(adfModelTypeMap.cosmosSql.linkedServiceType, adfModelTypeMap.cosmosSql.datasetType)(source)) {
        return (source.datasetSettings as CosmosSqlSettings)?.storeType === CosmosDBStoreType.Analytical;
    }
    return false;
};

export const isCassandraType = (sink: DataFlowSink) => {
    return isSinkTypeEq(adfModelTypeMap.cassandra.linkedServiceType, adfModelTypeMap.cassandra.datasetType)(sink);
};

export const isDataflowSinkEnableAlterRow = (sink: DataFlowSink, services: { dataflowFormatRegistryService: DataflowFormatRegistryService }): boolean => {
    if (!sink) {
        return false;
    }
    if (sink.isInlineDataset) {
        return services.dataflowFormatRegistryService.getEnableAlterRowInSink(sink.datasetSettings?.getFormat());
    } else if (sink.sinkDataset) {
        const dataset = sink.sinkDataset;
        const name = Metamodel.modelOf(dataset).name;
        // TODO: refactor this...
        return (
            name === adfModelTypeMap.azureSql.datasetType ||
            name === adfModelTypeMap.azureSqlDW.datasetType ||
            name === adfModelTypeMap.azureSqlMI.datasetType ||
            name === adfModelTypeMap.sqlServer.datasetType ||
            name === adfModelTypeMap.cosmosSql.datasetType ||
            name === adfModelTypeMap.snowflake.datasetType ||
            name === adfModelTypeMap.azurePostgreSQL.datasetType ||
            name === adfModelTypeMap.azureMySql.datasetType ||
            name === adfModelTypeMap.dynamics.datasetType ||
            name === adfModelTypeMap.dynamicsCRM.datasetType ||
            name === adfModelTypeMap.commonDataService.datasetType
        );
    }
    return false;
};

export const updateDatasetSettingsCache = (
    datasetSettings: DataFlowDatasetSettings,
    dataflowArguments: IDataFlowArguments
): void => {
    const expressions = Metamodel.modelOf(datasetSettings).attributes.reduce((previousValue, currentValue) => {
        const value = currentValue.value(datasetSettings);
        if (value instanceof DataFlowExpression && !!value.templateExpression) {
            return previousValue.concat(value);
        }
        if (Array.isArray(value) && value[0] instanceof DataFlowExpression) {
            return previousValue.concat(value.filter((exp) => !!exp?.templateExpression));
        }
        return previousValue;
    }, [] as DataFlowExpression[]);
    expressions.forEach((expression) => {
        if (GlobalVariables.validateDataFlowAPI) {
            asyncUpdateIdentifiers(dataflowArguments, expression, this);
        } else {
            updateIdentifiers(dataflowArguments, expression);
        }
    });
};

export const isAlternateKey = (value: unknown): boolean => {
    if (typeof value !== 'string') {
        return false;
    }
    return !stringIsNullOrEmpty(value.split('@')[1]) && !value.startsWith('@');
};

export const getMappedInputColumn = (
    outputAlias: string,
    outputType: IDataFlowDataType,
    columns: ColumnReference[]
): ColumnReference | undefined => {
    if (stringIsNullOrEmpty(outputAlias)) {
        return undefined;
    }
    const column = columns.find((col) => col.name.toLowerCase() === outputAlias.toLowerCase());
    if (column) {
        const commonType = DataFlowTypesUtils.getWiderType(column.column.dataFlowColumnType, outputType);
        if (!(commonType instanceof AnyDataFlowType)) {
            return column;
        }
    }
    return undefined;
};

const COSMOS_DB_NOSQL_AS_DEFAULT_KEY_COLUMN = '_rid';

export const initializeDataflowSink = (entity: DataFlowSink | DataFlowDatasetSettings): void => {
    const sink = entity instanceof DataFlowSink ? entity : entity.owner;
    if (!(sink instanceof DataFlowSink)) {
        return;
    }
    const source = findDataFlowSource(sink);

    // initialize update method
    if (isDataflowSourceEnableCdc(source) || isCosmosDBAnalyticalType(source)) {
        entity.allowInsert.expression = 'true';
        entity.allowUpsert.expression = 'true';
        entity.allowDelete.expression = 'true';
        entity.allowUpdate.expression = 'true';
    }

    // initialize key columns
    if (
        isCosmosDBAnalyticalType(source) &&
        source.schema.some((column) => column.name === COSMOS_DB_NOSQL_AS_DEFAULT_KEY_COLUMN)
    ) {
        entity.keyColumns = [
            sink.createTemplatableExpression(
                StringDataFlowType.TYPE,
                DataFlowUtils.escapeFieldIfNeeded(COSMOS_DB_NOSQL_AS_DEFAULT_KEY_COLUMN)
            )
        ];
    }
};

export const broadcastCDCChange = (dataflow: DataFlow, modelService: ModelService, messagingService: MessagingService): void => {
    modelService
        .relatedEntities(dataflow)
        .then((entities) =>
            entities.reduce((previousValue, currentValue) => {
                if (Metamodel.modelOf(currentValue)?.name === ModelConstants.pipeline) {
                    const pipeline = currentValue as Pipeline;
                    return previousValue.concat(
                        Array.from(
                            pipeline.activities.filter(
                                (activity) =>
                                    Metamodel.modelOf(activity)?.name === ModelConstants.executeDataFlowActivity &&
                                    (activity as ExecuteDataFlowActivity).dataflow.name === dataflow.name
                            )
                        )
                    );
                }
                return previousValue;
            }, [])
        )
        .then((activities: ExecuteDataFlowActivity[]) =>
            activities.forEach((activity) => {
                const dataflow = activity.dataflow;
                let newCdcValue;
                if (dataflow.useCdcConnector()) {
                    newCdcValue = generateUUID();
                    activity.customizedCheckpointKey = newCdcValue;
                } else {
                    activity.customizedCheckpointKey = undefined;
                }
                messagingService.broadcast(
                    MessagingServiceChannel.DataFlowCdcChange,
                    new Message(MessageKind.Empty, {
                        type: 'dataflowCdcChange',
                        entity: dataflow,
                        pipelineName: activity.owner.name,
                        newCdcValue
                    })
                );
            })
        )
        .catch(noop);
};
